import { useQuery } from '@apollo/client';
import { ContentCopy, Folder, MoreVert, type SvgIconComponent } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, MenuItem, Menu as MuiMenu } from '@mui/material';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDeleteDesignFolder, useUpdateDesignFolder } from '~/api/design-folders';
import { useDeleteDesign, useMoveDesign } from '~/api/designs';
import { useCopyDesign } from '~/api/designs/copy';
import { IconButton } from '~/components/IconButton';
import { MoveFolderDialog } from '~/components/dialogs/MoveFolderDialog';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  tableActionButtonProps,
  type GridActionCellProps,
} from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { usePaginationParams } from '~/hooks/pagination';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import { DesignMoveDocument, StudioMenuDesignFolder, StudioRenameFolderDialog } from '.';
import type { DesignList__Design, DesignList__DesignFolder } from '../DesignList.generated';
import { useDesignFiltersParams } from '../context/DesignFiltersContext/searchParams';

export interface StudioMenuProps extends GridActionCellProps {
  item: DesignList__Design | DesignList__DesignFolder;
  Icon?: SvgIconComponent;
}

export const StudioMenu = ({ item, Icon = MoreVert, ...cellProps }: StudioMenuProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [copy] = useCopyDesign();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<typeof item>();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [deleteDesign] = useDeleteDesign();
  const [deleteFolder] = useDeleteDesignFolder();
  const [moveDesign, moveDesignInfo] = useMoveDesign();
  const [updateDesignFolder, updateDesignFolderInfo] = useUpdateDesignFolder();
  const [moveDesignDialogOpen, setMoveDesignDialogOpen] = useState(false);
  const [moveFolderDialogOpen, setMoveFolderDialogOpen] = useState(false);
  const [renameFolderDialogOpen, setRenameFolderDialogOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() || '';
  const { tags, published } = useDesignFiltersParams();
  const { page, perPage } = usePaginationParams();

  const { data: designMoveData } = useQuery(DesignMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !moveDesignDialogOpen && !moveFolderDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      designMoveData?.network?.designFolders.filter((x) => x.canUpdate.value).map((x) => x.id) ??
      [],
    [designMoveData],
  );

  const onDuplicateDesign = async () => {
    if (!moreMenuProps.context?.id) return;
    const { data } = await copy({
      variables: { input: { designId: moreMenuProps.context.id }, page, perPage, search },
    });
    const designId = data?.copyDesign?.design.id;
    if (!designId) return;
    navigate({ pathname: `design/${designId}`, search: location.search });
  };

  const onMoveDesign = () => {
    assert(moreMenuProps.context != null, 'media: no context');
    setMoveDesignDialogOpen(true);
    moreMenuProps.onClose();
  };

  return (
    <>
      <IconButton
        label={t('pages.studio.list.actions.actions_tooltip')}
        {...tableActionButtonProps(mergeGridActionProps(moreTableActionProps(item), cellProps))}
        size="small"
        color="secondary"
      >
        <Icon />
      </IconButton>
      <MuiMenu {...moreMenuProps}>
        {item.__typename === 'Design' && (
          <MenuItem onClick={onMoveDesign}>
            <ListItemIcon>
              <Folder />
            </ListItemIcon>
            <ListItemText>{t('pages.studio.list.actions.move_to')}</ListItemText>
          </MenuItem>
        )}
        {item.__typename === 'Design' && (
          <MenuItem onClick={() => onDuplicateDesign()}>
            <ListItemIcon>
              <ContentCopy />
            </ListItemIcon>
            <ListItemText>{t('pages.studio.list.actions.duplicate')}</ListItemText>
          </MenuItem>
        )}
        {item.__typename === 'DesignFolder' && (
          <StudioMenuDesignFolder
            item={item}
            onMove={() => {
              assert(moreMenuProps.context != null, 'media: no context');
              setMoveFolderDialogOpen(true);
              moreMenuProps.onClose();
            }}
            onRename={() => {
              setRenameFolderDialogOpen(true);
              moreMenuProps.onClose();
            }}
          />
        )}

        <Divider />

        <MoreDeleteAction
          {...moreActionProps}
          disabled={!item.canDestroy.value}
          onClick={async () => {
            assert(moreMenuProps.context !== undefined);
            if (!(await confirmDelete())) return;
            if (item.__typename === 'DesignFolder') {
              await deleteFolder({
                variables: {
                  condition: { tags, published },
                  input: { designFolderId: moreMenuProps.context.id },
                },
              });
            } else {
              await deleteDesign({
                refetchQueries: ['DesignList'],
                variables: { designIds: [moreMenuProps.context.id] },
              });
            }
            moreMenuProps.onClose();
          }}
          tooltip={item.canDestroy.reasons?.fullMessages.join(', ')}
        />
      </MuiMenu>
      <MoveToFolderDialog
        editableFolderIds={editableFolderIds}
        onSubmit={async (targetFolderId) => {
          await moveDesign({
            refetchQueries: ['DesignList'],
            variables: {
              designFolderId: targetFolderId,
              designIds: [item.id],
            },
          });
        }}
        loading={moveDesignInfo.loading}
        onClose={() => setMoveDesignDialogOpen(false)}
        open={moveDesignDialogOpen}
        i18nKey="pages.studio.dialogs.move_design.title"
      />
      <MoveFolderDialog
        editableFolderIds={editableFolderIds}
        folderId={item.id}
        onSubmit={async (targetFolderId) => {
          await updateDesignFolder({
            refetchQueries: ['DesignList'],
            variables: {
              input: {
                designFolderId: item.id,
                patch: { parentId: targetFolderId },
              },
            },
          });
        }}
        loading={updateDesignFolderInfo.loading}
        onClose={() => setMoveFolderDialogOpen(false)}
        open={moveFolderDialogOpen}
        i18nKey="common.dialogs.move_folder.title"
      />
      {item.__typename === 'DesignFolder' && (
        <StudioRenameFolderDialog
          designFolder={item}
          onClose={() => setRenameFolderDialogOpen(false)}
          open={renameFolderDialogOpen}
        />
      )}
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.delete')}
        deleteConfirm
        prompt={
          <span>
            {item.__typename === 'DesignFolder'
              ? t('pages.studio.dialogs.delete_design.prompt_folder')
              : t('pages.studio.dialogs.delete_design.prompt_design')}
            {item.__typename === 'Design' &&
              item.publishedApp?.id &&
              ` ${t('pages.studio.dialogs.delete_design.prompt_published')}`}{' '}
            <Trans
              i18nKey="pages.studio.dialogs.delete_design.prompt_warning"
              components={{ 0: <b /> }}
            />
          </span>
        }
        title={t('pages.studio.dialogs.delete_design.title')}
      />
    </>
  );
};
